import { useEffect, useMemo, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { BADGES, MDC_DEGREES, STAGE_NAMES, stageFromAvgScore, weekStartMs } from '@erectus/shared';
import type { SessionSummary } from '@erectus/shared';
import { Button, Card, EmptyState, SectionTitle, Spinner } from '@/ui/primitives.js';
import { PageHeader } from '@/ui/Layout.js';
import { useAuth } from '@/stores/auth.js';
import { useWarm } from '@/lib/warmCache.js';
import { loaders, warmKeys } from '@/lib/warmup.js';
import { Character, StageStrip, type Stage } from './Character.js';
import { DarwinPicks } from './DarwinPicks.js';
import { SessionHistory } from './SessionHistory.js';
import { ActivityCalendar } from './ActivityCalendar.js';
import { TodayPanel } from '@/features/program/TodayPanel.js';
import { useProgramToday } from '@/features/program/useProgram.js';
import { PostureCheckCard } from '@/features/check/PostureCheckCard.js';
import type { ExerciseLogEntry } from '@/lib/repo.js';

/**
 * Progress (docs/PRODUCT.md §2 screen 7).
 *
 * Every trend statement respects the ±5° measurement floor: a change inside that
 * band is reported as "maintained", not as improvement (DATA_AND_ML §2).
 */
type Session = SessionSummary & { id: string };

/**
 * Loading must be observable. A bare `sessions === null` spinner turns any
 * stalled query into an app that hangs forever with no explanation — which is
 * exactly what happened on a phone with no sessions yet. Failure and timeout
 * are distinct states that say something useful (the loader times out).
 *
 * Normally the splash has fetched the history already (lib/warmup.ts), and the
 * first render is the finished screen.
 */
export function ProgressPage(): ReactNode {
  const { user, userDoc } = useAuth();
  const today = useProgramToday();
  const [warmState, , retry] = useWarm<Session[]>(
    user ? warmKeys.sessions(user.uid) : null,
    () => loaders.sessions(user!.uid),
    'Could not load your history.',
  );
  const load =
    warmState.status === 'ready' ? { status: 'ready' as const, sessions: warmState.data } : warmState;
  // Exercise days for the Activity calendar; an empty log is fine while it loads.
  const [logState] = useWarm<ExerciseLogEntry[]>(
    user ? warmKeys.exerciseLog(user.uid) : null,
    () => loaders.exerciseLog(user!.uid),
    'Could not load your exercise log.',
  );
  const exerciseLog = logState.status === 'ready' ? logState.data : [];

  const sessions = load.status === 'ready' ? load.sessions : null;

  const weeks = useMemo(() => groupByWeek(sessions ?? []), [sessions]);
  const avg4w = useMemo(() => averageScore(sessions ?? [], 28), [sessions]);
  const stage: Stage = userDoc?.gamification?.stage ?? stageFromAvgScore(avg4w);
  const cvaTrend = useMemo(() => cvaChange(sessions ?? []), [sessions]);
  const earned = new Set(userDoc?.gamification?.badges ?? []);

  // A reminder tap lands on /progress#darwin-picks: scroll that card into view
  // once the history is on screen.
  const { hash } = useLocation();
  const ready = load.status === 'ready';
  useEffect(() => {
    if (!ready || !hash) return;
    const target = document.getElementById(hash.slice(1));
    if (!target) return;
    const timer = window.setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 150);
    return () => window.clearTimeout(timer);
  }, [hash, ready]);

  if (load.status === 'loading') {
    return (
      <div className="space-y-4">
        <PageHeader title="Progress" />
        <Card className="grid place-items-center py-8">
          <Spinner label="Loading your history" />
        </Card>
      </div>
    );
  }

  if (load.status === 'failed') {
    return (
      <div className="space-y-4">
        <PageHeader title="Progress" />
        <EmptyState
          title="Could not load your history"
          message={load.message}
          action={
            <Button variant="secondary" onClick={() => retry()}>
              Try again
            </Button>
          }
        />
      </div>
    );
  }

  if (sessions === null || sessions.length === 0) {
    return (
      <div className="space-y-4">
        <PageHeader title="Progress" subtitle="No sessions yet." />
        <TodayPanel today={today} compact />
        <StartSessionCard />
        {user ? <PostureCheckCard uid={user.uid} /> : null}
        <EmptyState
          title="Nothing to compare yet"
          message="Run one session, then come back next week — the trend is the point, not a single reading."
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Progress" subtitle={`${sessions.length} sessions recorded`} />
      <TodayPanel today={today} compact />
      <ActivityCalendar sessions={sessions} exerciseLog={exerciseLog} />
      {user ? <PostureCheckCard uid={user.uid} /> : null}

      <Card className="flex items-center gap-4">
        <Character stage={stage} size={96} />
        <div>
          <p className="text-xs text-muted">Stage {stage} of 5</p>
          <p className="text-lg font-bold">{STAGE_NAMES[stage]}</p>
          <p className="mt-1 text-xs text-muted">
            Moves on your 4-week average ({avg4w.toFixed(0)}), so a single rough session never sets
            you back.
          </p>
        </div>
      </Card>

      <DarwinPicks sessions={sessions} />

      <Card>
        <SectionTitle>Weekly score</SectionTitle>
        <WeeklyChart weeks={weeks} />
      </Card>

      <Card>
        <SectionTitle>Neck angle trend</SectionTitle>
        {cvaTrend === null ? (
          <p className="text-sm text-muted">Two sessions are needed before a trend means anything.</p>
        ) : (
          <div className="space-y-2">
            <p className="text-2xl font-bold">
              {cvaTrend.label === 'maintained'
                ? 'Maintained'
                : `${cvaTrend.delta > 0 ? '+' : ''}${cvaTrend.delta.toFixed(1)}°`}
            </p>
            <p className="text-sm text-ink-soft">
              {cvaTrend.label === 'maintained'
                ? `Change is within the ±${MDC_DEGREES}° measurement margin, so we call it unchanged.`
                : cvaTrend.delta > 0
                  ? 'Your head is sitting further back than it was. Keep the setup identical to be sure.'
                  : 'Your head is sitting further forward than it was.'}
            </p>
            <CvaBand sessions={sessions} />
            <Link to="/sources" className="block text-xs text-muted underline">
              Why a ±{MDC_DEGREES}° margin
            </Link>
          </div>
        )}
      </Card>

      <Card>
        <SectionTitle>Stages</SectionTitle>
        <StageStrip current={stage} />
      </Card>

      <Card>
        <SectionTitle>Badges</SectionTitle>
        <ul className="grid grid-cols-2 gap-2">
          {BADGES.map((badge) => {
            const has = earned.has(badge.id);
            return (
              <li
                key={badge.id}
                className={`rounded-xl border px-3 py-2 ${
                  has ? 'border-ink bg-surface' : 'border-line opacity-50'
                }`}
              >
                <p className="text-sm font-semibold">{badge.name}</p>
                <p className="text-xs text-muted">{badge.description}</p>
              </li>
            );
          })}
        </ul>
        {userDoc?.gamification?.weeklyStreak ? (
          <p className="mt-3 text-sm">
            Weekly streak: <strong>{userDoc.gamification.weeklyStreak}</strong>
          </p>
        ) : null}
      </Card>

      <SessionHistory sessions={sessions} />
    </div>
  );
}

interface WeekBucket {
  weekStart: number;
  avgScore: number;
  sessionCount: number;
}

export function groupByWeek(sessions: readonly Session[]): WeekBucket[] {
  const map = new Map<number, { total: number; count: number }>();
  for (const session of sessions) {
    const key = weekStartMs(session.startedAt);
    const bucket = map.get(key) ?? { total: 0, count: 0 };
    bucket.total += session.score;
    bucket.count += 1;
    map.set(key, bucket);
  }
  return [...map.entries()]
    .map(([weekStart, { total, count }]) => ({
      weekStart,
      avgScore: total / count,
      sessionCount: count,
    }))
    .sort((a, b) => a.weekStart - b.weekStart)
    .slice(-12);
}

export function averageScore(sessions: readonly Session[], days: number): number {
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  const recent = sessions.filter((s) => s.startedAt >= cutoff);
  if (recent.length === 0) return 0;
  return recent.reduce((sum, s) => sum + s.score, 0) / recent.length;
}

/**
 * Compare the newest CVA mean with the previous one, honouring the ±5° floor.
 * Sessions before thresholds v2 have no `cva` and are skipped, never mixed in.
 */
export function cvaChange(
  sessions: readonly Session[],
): { delta: number; label: 'up' | 'down' | 'maintained' } | null {
  const withCva = sessions.filter((s) => s.cva != null).slice(0, 2);
  if (withCva.length < 2) return null;
  const delta = (withCva[0]!.cva?.mean ?? 0) - (withCva[1]!.cva?.mean ?? 0);
  if (Math.abs(delta) <= MDC_DEGREES) return { delta, label: 'maintained' };
  return { delta, label: delta > 0 ? 'up' : 'down' };
}

/**
 * The first-run call to action, shown only while there are no sessions yet.
 *
 * Landing straight in the camera was wrong: opening the app immediately asked
 * for the camera and filled the screen with a live preview before the user had
 * decided to do anything. Starting a session is a deliberate choice — once
 * there is history, the Session tab is that choice and this card would only
 * push the Activity calendar and Darwin's picks down the screen.
 */
function StartSessionCard(): ReactNode {
  return (
    <Card className="flex items-center justify-between gap-3">
      <div>
        <p className="font-semibold">Start a session</p>
        <p className="text-xs text-muted">Phone at your side, then sit down.</p>
      </div>
      <Link
        to="/capture"
        className="shrink-0 rounded-xl bg-ink px-4 py-2.5 text-sm font-semibold text-white"
      >
        Set up
      </Link>
    </Card>
  );
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;
const CHART_WEEKS = 6;

/**
 * Average session score per week, the last six weeks including empty ones,
 * so the bars read as a timeline. Bars size against a fixed-height track: a
 * percentage height inside an auto-height flex column resolves to nothing,
 * which is why this chart used to show only its date labels.
 *
 * Week buckets start Monday 00:00 UTC (weekStartMs), so labels format in UTC
 * too — formatting locally showed the Sunday before in the Americas.
 */
function WeeklyChart({ weeks }: { weeks: WeekBucket[] }): ReactNode {
  if (weeks.length === 0) return null;
  const byStart = new Map(weeks.map((w) => [w.weekStart, w]));
  const thisWeek = weekStartMs(Date.now());
  const slots = Array.from({ length: CHART_WEEKS }, (_, i) => thisWeek - (CHART_WEEKS - 1 - i) * WEEK_MS);
  const label = (ms: number): string =>
    new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });

  // Trend line through the bar tops of weeks that have sessions. The grid has
  // no gaps (each cell pads its own bar), so a column's centre is exactly
  // (i + ½) / N of the width and the line meets the bars.
  const points = slots
    .map((start, i) => {
      const week = byStart.get(start);
      return week ? { x: ((i + 0.5) / CHART_WEEKS) * 100, y: 100 - Math.max(4, Math.min(100, week.avgScore)) } : null;
    })
    .filter((p): p is { x: number; y: number } => p !== null);

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted">Average session score per week, out of 100. The line is the trend.</p>
      <div className="grid text-center" style={{ gridTemplateColumns: `repeat(${CHART_WEEKS}, minmax(0, 1fr))` }}>
        {slots.map((start) => {
          const week = byStart.get(start);
          return (
            <span key={start} className="h-4 text-xs font-semibold text-ink">
              {week ? Math.round(week.avgScore) : ''}
            </span>
          );
        })}
      </div>
      <div className="relative h-20">
        <div className="grid h-full" style={{ gridTemplateColumns: `repeat(${CHART_WEEKS}, minmax(0, 1fr))` }}>
          {slots.map((start) => {
            const week = byStart.get(start);
            return (
              <div key={start} className="h-full px-1">
                <div className="relative h-full w-full overflow-hidden rounded-md bg-surface">
                  {week ? (
                    <div
                      className="absolute inset-x-0 bottom-0 rounded-md bg-ink/25"
                      style={{ height: `${Math.max(4, Math.min(100, week.avgScore))}%` }}
                      role="img"
                      aria-label={`Week of ${label(start)}: average score ${Math.round(week.avgScore)} from ${week.sessionCount} session${week.sessionCount === 1 ? '' : 's'}`}
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        {points.length >= 2 ? (
          <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden>
            <polyline
              points={points.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="var(--color-ink)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        ) : null}
        {points.map((p) => (
          <span
            key={p.x}
            aria-hidden
            className="pointer-events-none absolute size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-ink"
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
          />
        ))}
      </div>
      <div className="grid text-center" style={{ gridTemplateColumns: `repeat(${CHART_WEEKS}, minmax(0, 1fr))` }}>
        {slots.map((start) => (
          <span key={start} className={`text-[10px] ${start === thisWeek ? 'font-semibold text-ink' : 'text-muted'}`}>
            {start === thisWeek ? 'This week' : label(start)}
          </span>
        ))}
      </div>
    </div>
  );
}

/** CVA per session with a ±5° error band, oldest on the left. */
function CvaBand({ sessions }: { sessions: readonly Session[] }): ReactNode {
  const points = sessions
    .filter((s) => s.cva != null)
    .slice(0, 8)
    .reverse()
    .map((s) => s.cva!.mean);
  if (points.length < 2) return null;
  const min = Math.min(...points) - 8;
  const max = Math.max(...points) + 8;
  const scale = (v: number): number => 100 - ((v - min) / (max - min)) * 100;
  const step = 100 / (points.length - 1);
  const line = points.map((v, i) => `${i * step},${scale(v)}`).join(' ');
  const upper = points.map((v, i) => `${i * step},${scale(v + MDC_DEGREES)}`).join(' ');
  const lower = points
    .map((v, i) => `${i * step},${scale(v - MDC_DEGREES)}`)
    .reverse()
    .join(' ');

  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="h-24 w-full" role="img" aria-label="Neck angle per session with a plus or minus five degree band">
      <polygon points={`${upper} ${lower}`} fill="var(--color-state-good-soft)" />
      <polyline points={line} fill="none" stroke="var(--color-ink)" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}
